import { Clipboard } from "@capacitor/clipboard";
import { Trans } from '@lingui/macro';
import { useEffect, useState } from "react";
import { SoribotTestRun } from "../../../models/SoribotTestCase";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../../components/ui/dialog";
import { Textarea } from "../../../components/ui/textarea";
import { Switch } from "../../../components/ui/switch";
import { Label } from "../../../components/ui/label";

export const TestRunExportModal: React.FC<{ run: SoribotTestRun }> = ({ run }) => {
  const [show, setShow] = useState(false);
  const [template, setTemplate] = useState('');
  const [text, setText] = useState('');
  const [includeEval, setIncludeEval] = useState(false);

  function onPresent() {
    updateText(startingTemplate());
  }

  useEffect(() => {
    updateText(startingTemplate());
  }, [includeEval])

  const handleOpenChange = (open: boolean) => {
    setShow(open);
    if (open) {
      onPresent();
    }
  };

  return (
    <Dialog open={show} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost"><Trans>Export</Trans></Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle><Trans>Export Questions and Answers</Trans></DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          <div className="flex items-center justify-between">
            <Label className="font-bold"><Trans>Export Format</Trans></Label>
            <div className="flex items-center gap-2">
              <Switch id="include-eval" checked={includeEval} onCheckedChange={setIncludeEval} />
              <Label htmlFor="include-eval"><Trans>Include Evaluation</Trans></Label>
            </div>
          </div>

          <Textarea
            value={template}
            onChange={e => updateText(e.target.value)}
            className="min-h-[100px] font-mono text-sm"
          />

          <div className="flex items-center justify-between mt-4">
            <Label className="font-bold"><Trans>Exported Text</Trans></Label>
            <Button size="sm" onClick={copyToClipboard}><Trans>Copy to clipboard</Trans></Button>
          </div>

          <Textarea
            value={text}
            readOnly
            className="flex-1 min-h-[300px] font-mono text-sm"
          />
        </div>
      </DialogContent>
    </Dialog>
  );

  async function copyToClipboard() {
    await Clipboard.write({ string: text });
    setShow(false);
  }

  function updateText(template: string) {
    setTemplate(template);
    const body = run.answers.map(a => {
      const answer = a.botMessage?.llmResponse || a.answer || 'N/A';
      const question = a.question;
      const score = a.score?.toString() || 'N/A';
      const feedback = a.feedback ?? 'N/A';
      return template.replace('{answer}', answer).replace('{question}', question).replace('{score}', score).replace('{feedback}', feedback).replaceAll('\\t', '\t');
    }).join('\n');
    setText(`${run.testCase.title}\n\n${body}`);
  }

  function startingTemplate(): string {
    let template = `Q:\n{question}\n\nA:\n{answer}`;

    if (includeEval && run.answers.some(a => a.score)) {
      template += `\n\nE: {score} ★\n{feedback}`;
    }
    return template + '\n\n\n';
  }
}
